import React, { useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import routes from './router/RouteConfig';
import RouteElement from './router/RouteElement';
import { ConfigContext, LocaleContext } from './contexts';
import { analytics } from './analytics';
import i18next from 'i18next';
import './i18next/i18n';
import { SunSpinner } from './sharedComponents/SunSpinner/SunSpinner';
import Typography from './sharedComponents/Typography/Typography';
import TextButton from './sharedComponents/TextButton/TextButton';
import { useTransitionState } from 'react-transition-state';
import styles from './app.module.scss';
import useProtectUser from './hooks/useProtectUser';
import useCookiebotConsent from './hooks/useCookiebotConsent';

const App: React.FC = () => {
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);
  const hasStatisticsConsent = useCookiebotConsent('statistics');
  const {
    ready: appIsReady,
    error: appLoadingError,
    user,
    policyBundles,
  } = useProtectUser();

  const { config } = useContext(ConfigContext);

  const browserLanguageDetector = (): string => {
    const lang = navigator.language;
    if (lang.includes('fr')) {
      return 'fr-FR';
    } else if (lang.includes('sv')) {
      return 'sv-SE';
    } else {
      return 'en-US';
    }
  };

  const [{ status }, toggle] = useTransitionState({
    timeout: 1000,
    initialEntered: true,
    preEnter: true,
  });

  useEffect(() => {
    if (config && hasStatisticsConsent && !analyticsLoaded) {
      const { rudderstackKey } = config;
      analytics.load(
        rudderstackKey,
        'https://sensiblewel.dataplane.rudderstack.com',
      );

      setAnalyticsLoaded(true);
    }
  }, [config, hasStatisticsConsent, analyticsLoaded]);

  if (user?.id) {
    void i18next.changeLanguage(user.langLocale);
  } else {
    const lang = browserLanguageDetector();
    void i18next.changeLanguage(lang);
  }

  //TODO: #1906 temp solution - replace once localization is in place
  const localeEnvelope = useContext(LocaleContext);
  useEffect(() => {
    if (appIsReady) toggle();
  }, [appIsReady]);

  useEffect(() => {
    //TODO: #1906 temp solution - replace once localization is in place
    if (policyBundles?.length) {
      const bundleCurrency = policyBundles[0].currency;
      localeEnvelope.actions.updateLocale(bundleCurrency);
    }
  }, [localeEnvelope.actions, policyBundles]);

  const LoadingErrorState = (
    <div className={styles.appErrorScreen}>
      <div className={styles.appErrorCard}>
        <Typography variant="h2" className={styles.appErrorTitle}>
          {i18next.t('error.internal')}
        </Typography>
        <Typography variant="body-1" className={styles.appErrorBody}>
          {i18next.t('error.customer_support')}
        </Typography>
        <div className={styles.appErrorActions}>
          <TextButton
            label="Reload page"
            clickHandling={(): void => window.location.reload()}
          />
          <TextButton
            label={i18next.t('generic.label.customer_support_email')}
            clickHandling={{ url: i18next.t('generic.contact_support.link') }}
            inline
          />
        </div>
      </div>
    </div>
  );

  const SpinnerLoader = (
    <div className={[styles.basicTransition, styles[status]].join(' ')}>
      <div className={styles.basicTransitionInner}>
        <SunSpinner rays={8} />
      </div>
    </div>
  );

  if (appLoadingError) {
    return LoadingErrorState;
  }

  return (
    <>
      {SpinnerLoader}
      <Routes>
        <>
          {routes.map((route) => {
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<RouteElement route={route} />}
              />
            );
          })}
          <Route path="*" element={<Navigate to="/trips" />} />
        </>
      </Routes>
    </>
  );
};

export default App;
